(() => {
  const MAX_FREE = 4;
  const DEBUG = '[SampleEnforcer]';
  const ENFORCE_DELAY_MS = 300;
  const SOFT_LOCK_TTL_MS = 2000;

  let isRunning = false;
  let scheduled = null;

  const log = (...a) => console.log(DEBUG, ...a);
  const err = (...a) => console.error(DEBUG, ...a);

  /* =======================
     Soft lock
  ======================= */
  const LOCK_KEY = '__sample_enforcer_lock__';

  const lock = () => {
    try {
      sessionStorage.setItem(LOCK_KEY, Date.now() + SOFT_LOCK_TTL_MS);
    } catch (_) {}
  };

  const isLocked = () => {
    try {
      return Date.now() < Number(sessionStorage.getItem(LOCK_KEY) || 0);
    } catch {
      return false;
    }
  };

  /* =======================
     Network helpers
  ======================= */
  const getCart = () =>
    fetch('/cart.js', { cache: 'no-store' }).then(r => r.json());

  const changeItem = (key, quantity) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });

  const addItem = (variantId, quantity) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity })
    });

  /* =======================
     api-cart-data
  ======================= */
  async function loadApiCartData() {
    const html = await fetch('/?section_id=api-cart-data', {
      cache: 'no-store'
    }).then(r => r.text());

    const el = document.createElement('div');
    el.innerHTML = html;
    const section = el.querySelector('#shopify-section-api-cart-data');
    if (!section) return {};

    try {
      return JSON.parse(section.textContent).items || {};
    } catch {
      return {};
    }
  }

  /* =======================
     Scheduler
  ======================= */
  function schedule(reason) {
    if (isLocked()) return;
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => enforce(reason), ENFORCE_DELAY_MS);
  }

  /* =======================
     Enforcement
  ======================= */
  async function enforce(reason) {
    if (isRunning || isLocked()) return;
    isRunning = true;

    console.group(`${DEBUG} enforce (${reason})`);

    try {
      const api = await loadApiCartData();
      const cart = await getCart();

      // 1. Filter cart to find only Sample items
      //    We keep the 'cfg' (config) attached so we can find premium IDs later
      const sampleItems = cart.items
        .map(item => ({ item, cfg: api[item.handle] }))
        .filter(x => x.cfg && x.cfg.is_sample);

      let didChange = false;
      let runningTotal = 0;

      for (const { item, cfg } of sampleItems) {
        
        // A. How many slots available globally?
        const spaceLeft = Math.max(MAX_FREE - runningTotal, 0);

        // B. How many of THIS item can stay Free?
        //    If spaceLeft is 0, this is 0.
        //    If Item qty is 5 and space is 4, this is 4.
        const qtyToKeepFree = Math.min(item.quantity, spaceLeft);

        // C. How many are overflowing?
        const qtyOverflow = item.quantity - qtyToKeepFree;

        // D. Update running total for the NEXT item in loop
        runningTotal += qtyToKeepFree;

        if (qtyOverflow > 0) {
          log(`Processing ${item.handle}: Total ${item.quantity}, Free ${qtyToKeepFree}, Overflow ${qtyOverflow}`);

          // DECISION: Convert to Premium OR Delete?
          // We convert to premium IF:
          // 1. The item has at least 1 unit in the "Free" bucket (qtyToKeepFree > 0).
          //    This implies it's a "Bulk" purchase of a valid sample.
          // 2. A premium variant ID exists.
          
          const premiumId = cfg.premium?.variant_id;
          const shouldConvertToPremium = (qtyToKeepFree > 0) && premiumId;

          if (shouldConvertToPremium) {
            log(`--> Converting ${qtyOverflow} overflow to Premium`);
            // 1. Reduce the Free item to the cap
            await changeItem(item.key, qtyToKeepFree);
            // 2. Add the Overflow as Premium
            await addItem(premiumId, qtyOverflow);
          } else {
            log(`--> Blocking/Deleting ${qtyOverflow} overflow (No free slots allocated or no premium ID)`);
            // Just reduce the item to 0 (or whatever fit), effectively deleting the excess
            await changeItem(item.key, qtyToKeepFree);
          }

          didChange = true;
        }
      }

      if (didChange) {
        log('Changes applied. Reloading.');
        lock();
        window.location.reload();
      } else {
        log(`Samples usage: ${runningTotal}/${MAX_FREE}. OK.`);
      }

    } catch (e) {
      err('Enforcement error', e);
    } finally {
      console.groupEnd();
      isRunning = false;
    }
  }

  /* =======================
     Triggers
  ======================= */
  document.addEventListener('DOMContentLoaded', () => schedule('load'));

  const origFetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await origFetch(...args);
    const url = args[0]?.toString() || '';
    if (url.includes('/cart/')) schedule(`fetch:${url}`);
    return res;
  };

  document.addEventListener(
    'change',
    e => {
      if (
        e.target?.matches(
          'input[name="updates[]"], input[data-quantity-input]'
        )
      ) {
        schedule('qty-change');
      }
    },
    true
  );

  document.addEventListener(
    'click',
    e => {
      if (e.target.closest('[data-add-quantity],[data-subtract-quantity]')) {
        schedule('qty-click');
      }
    },
    true
  );
})();